import React, {
  useRef,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import { Container, Graphics } from "@pixi/react";
import anime from "animejs";
import { USER_INTERFACE_BUTTON_CONFIG } from "../../../../textureConfigs";
import { PixiSettings } from "../../../../pixiSettings";
import SliderButton from "../Buttons/SliderButton";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import SymbolsSlide from "./SymbolsSlide";
import PaylinesSlide from "./PaylinesSlide";
import FeaturesSlide from "./FeaturesSlide";

const ViewportMask = React.forwardRef(({ width, height }, ref) => (
  <Graphics
    ref={ref}
    name="paytable_viewport_mask"
    draw={(g) => {
      g.clear();
      g.beginFill(0x000000, 1);
      g.drawRect(-width / 2, 0, width, height);
      g.endFill();
    }}
  />
));

const PaytableSlider = ({ viewportConfig, totalSlides = 1 }) => {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const slidesContainerRef = useRef(null);
  const viewportMaskRef = useRef(null);
  const animationRef = useRef(null);
  const isJumpingRef = useRef(false);
  const slideRefsRef = useRef({}); // Store refs to all slide containers

  // Drag/swipe state refs
  const dragStartX = useRef(null);
  const dragCurrentX = useRef(null);
  const isDragging = useRef(false);
  const dragStartTime = useRef(null);
  const dragStartContainerX = useRef(null);

  const { getResponsiveConfig, device, orientation, uiSymbolScale } =
    useDimensions();

  // Get button config for pagination positioning
  const buttonConfig = useMemo(
    () => getResponsiveConfig(USER_INTERFACE_BUTTON_CONFIG),
    [getResponsiveConfig]
  );

  // Helper function to determine slide component and props based on actual slide index
  const getSlideInfo = useCallback((slideIndex) => {
    let SlideComponent, slideType;

    if (slideIndex <= 1) {
      SlideComponent = SymbolsSlide;
      slideType = "symbols";
    } else if (slideIndex <= 4) {
      SlideComponent = FeaturesSlide;
      slideType = "features";
    } else {
      SlideComponent = PaylinesSlide;
      slideType = "paylines";
    }

    return { SlideComponent, slideType, slideIndex };
  }, []);

  // Calculate slide x position
  const getSlideX = useCallback(
    (position) =>
      Math.round(viewportConfig.maxWidth / 2 + position * viewportConfig.maxWidth),
    [viewportConfig.maxWidth]
  );

  // Calculate slide alpha based on distance from center
  const calculateSlideAlpha = useCallback((slideContainer) => {
    if (!slideContainer || !slidesContainerRef.current) return 1;

    // Get slide's global X position
    const slideGlobalX = slidesContainerRef.current.x + slideContainer.x;

    // Distance from center (0 is center)
    const distanceFromCenter = Math.abs(slideGlobalX);

    // Fade distance threshold - start fading when slide is this far from center
    const fadeStartDistance = viewportConfig.maxWidth * 0.1;
    const fadeEndDistance = viewportConfig.maxWidth * 0.4;

    if (distanceFromCenter <= fadeStartDistance) {
      return 1; // Fully visible
    } else if (distanceFromCenter >= fadeEndDistance) {
      return 0; // Fully invisible
    } else {
      // Linear fade between start and end
      const fadeProgress = (distanceFromCenter - fadeStartDistance) / (fadeEndDistance - fadeStartDistance);
      return 1 - fadeProgress;
    }
  }, [viewportConfig.maxWidth]);

  // Update all slide alphas based on their current positions
  const updateSlideAlphas = useCallback(() => {
    Object.values(slideRefsRef.current).forEach((slideRef) => {
      if (slideRef) {
        const newAlpha = calculateSlideAlpha(slideRef);
        slideRef.alpha = newAlpha;
      }
    });
  }, [calculateSlideAlpha]);

  // Render a slide container
  const renderSlide = useCallback(
    (slideIndex, position, key) => {
      const { SlideComponent, slideType } = getSlideInfo(slideIndex);
      return (
        <Container
          key={key}
          name={`paytable_slide_${key}`}
          x={getSlideX(position)}
          y={0}
          ref={(ref) => {
            if (ref) {
              slideRefsRef.current[key] = ref;
            }
          }}
        >
          <SlideComponent
            viewportConfig={viewportConfig}
            slideIndex={slideIndex}
            slideType={slideType}
          />
        </Container>
      );
    },
    [getSlideInfo, getSlideX, viewportConfig]
  );

  // Calculate slides container position based on current slide
  // Account for the leading clone slide (shift by +1)
  const slidesContainerX = useMemo(
    () =>
      -viewportConfig.maxWidth / 2 -
      (currentSlideIndex + 1) * viewportConfig.maxWidth,
    [currentSlideIndex, viewportConfig.maxWidth]
  );

  // Handle next slide (circular with clone slides)
  const handleNext = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlideIndex((prev) => prev + 1);
  }, [isTransitioning]);

  // Handle previous slide (circular with clone slides)
  const handlePrevious = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlideIndex((prev) => prev - 1);
  }, [isTransitioning]);

  // Handle pagination dot click
  const handleDotClick = useCallback((index) => {
    if (isDragging.current) return; // Prevent click during drag
    setCurrentSlideIndex(index);
  }, []);

  // Drag/Swipe handlers
  const handlePointerDown = useCallback((event) => {
    if (isTransitioning) return;

    const startX = event.global.x;
    const containerX = slidesContainerRef.current?.x || slidesContainerX;

    isDragging.current = true;
    dragStartX.current = startX;
    dragCurrentX.current = startX;
    dragStartTime.current = Date.now();
    dragStartContainerX.current = containerX;

    // Change cursor to grabbing
    if (slidesContainerRef.current) {
      slidesContainerRef.current.cursor = "grabbing";
    }
  }, [isTransitioning, slidesContainerX]);

  const handlePointerMove = useCallback((event) => {
    if (!isDragging.current || !slidesContainerRef.current) return;

    dragCurrentX.current = event.global.x;
    const dragDelta = dragCurrentX.current - dragStartX.current;

    // Update container position in real-time
    slidesContainerRef.current.x = dragStartContainerX.current + dragDelta;

    // Update slide alphas during drag
    updateSlideAlphas();
  }, [updateSlideAlphas]);

  const handlePointerUp = useCallback(() => {
    if (!isDragging.current) {
      return;
    }

    const dragDistance = dragCurrentX.current - dragStartX.current;
    const dragDuration = Date.now() - dragStartTime.current;
    const velocity = Math.abs(dragDistance / dragDuration);

    // Swipe thresholds
    const SWIPE_THRESHOLD = 50; // pixels
    const VELOCITY_THRESHOLD = 0.3; // pixels per ms

    // Reset cursor
    if (slidesContainerRef.current) {
      slidesContainerRef.current.cursor = "grab";
    }

    // Reset dragging flag first
    isDragging.current = false;

    // Determine if it's a valid swipe
    if (Math.abs(dragDistance) > SWIPE_THRESHOLD || velocity > VELOCITY_THRESHOLD) {
      // Valid swipe - trigger navigation
      if (dragDistance > 0) {
        // Swiped right - go to previous slide
        handlePrevious();
      } else {
        // Swiped left - go to next slide
        handleNext();
      }
    } else {
      // Not a valid swipe - snap back to current position

      if (slidesContainerRef.current) {
        anime({
          targets: slidesContainerRef.current,
          x: slidesContainerX,
          duration: 300,
          easing: "easeOutQuad",
          update: () => {
            updateSlideAlphas();
          },
        });
      }
    }

    // Reset drag state
    dragStartX.current = null;
    dragCurrentX.current = null;
    dragStartTime.current = null;
    dragStartContainerX.current = null;
  }, [slidesContainerX, handleNext, handlePrevious, updateSlideAlphas]);

  // Animate slides container position with elastic bounce effect
  useEffect(() => {
    // Don't animate if user is dragging
    if (isDragging.current) return;

    if (slidesContainerRef.current && animationRef.current) {
      anime.remove(animationRef.current);
    }

    if (slidesContainerRef.current) {
      // Check if we're jumping from clone to real slide
      if (isJumpingRef.current) {
        // Instant jump without animation
        slidesContainerRef.current.x = slidesContainerX;
        isJumpingRef.current = false;
        setIsTransitioning(false);
        updateSlideAlphas();
      } else {
        // Normal animation - faster and smoother
        animationRef.current = anime({
          targets: slidesContainerRef.current,
          x: slidesContainerX,
          duration: 300,
          easing: "easeOutCubic",
          begin: () => {
            // Update alphas at the start of animation to fix initial render
            updateSlideAlphas();
          },
          update: () => {
            updateSlideAlphas();
          },
          complete: () => {
            // Allow next transition immediately when animation completes
            setIsTransitioning(false);

            // After animation completes, check if we're on a clone and jump to real slide
            if (currentSlideIndex >= totalSlides) {
              // On clone of first slide (after last slide), jump to real first slide
              isJumpingRef.current = true;
              setCurrentSlideIndex(0);
            } else if (currentSlideIndex < 0) {
              // On clone of last slide (before first slide), jump to real last slide
              isJumpingRef.current = true;
              setCurrentSlideIndex(totalSlides - 1);
            }
          },
        });
      }
    }

    return () => {
      if (animationRef.current) {
        anime.remove(animationRef.current);
      }
    };
  }, [slidesContainerX, currentSlideIndex, totalSlides, updateSlideAlphas]);

  // Apply mask to slides container
  useEffect(() => {
    if (viewportMaskRef.current && slidesContainerRef.current) {
      slidesContainerRef.current.mask = viewportMaskRef.current;
    }
  }, []);

  // Set initial position on mount only
  useEffect(() => {
    if (slidesContainerRef.current) {
      slidesContainerRef.current.x = slidesContainerX;
      updateSlideAlphas();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Initialize slide alphas on mount and when slides change
  useEffect(() => {
    updateSlideAlphas();
  }, [updateSlideAlphas]);

  // Get pagination configuration (size, spacing, and position for all devices/orientations)
  const getPaginationConfig = useCallback(() => {
    const baseDotSize = 16; // Base size for desktop
    let dotSizeMultiplier = 1.0;
    let dotSpacingMultiplier = 2.0; // Spacing is 2x dot size
    let paginationYMultiplier = 0.5; // Y position multiplier

    if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        dotSizeMultiplier = 2.0;
        dotSpacingMultiplier = 2.0;
        paginationYMultiplier = 0;
      } else {
        // Landscape
        dotSizeMultiplier = 2.0;
        dotSpacingMultiplier = 2.0;
        paginationYMultiplier = 0.5;
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        dotSizeMultiplier = 2.25;
        dotSpacingMultiplier = 2.0;
        paginationYMultiplier = 0;
      } else {
        // Landscape
        dotSizeMultiplier = 1.85;
        dotSpacingMultiplier = 2.0;
        paginationYMultiplier = 0;
      }
    } else {
      // Desktop
      dotSizeMultiplier = 1.25;
      dotSpacingMultiplier = 2.0;
      paginationYMultiplier = 1.0;
    }

    // Calculate responsive dot size
    const responsiveDotSize = baseDotSize * dotSizeMultiplier * uiSymbolScale;
    const dotSize = Math.round(responsiveDotSize);
    const dotSpacing = Math.round(dotSize * dotSpacingMultiplier);

    // Calculate pagination position
    const totalDotsWidth =
      totalSlides * dotSize + (totalSlides - 1) * dotSpacing;
    const paginationStartX = Math.round(-totalDotsWidth / 2);
    const paginationY = Math.round(
      viewportConfig.viewportHeight +
        buttonConfig.height * paginationYMultiplier
    );

    return { paginationY, paginationStartX, dotSize, dotSpacing };
  }, [
    device,
    orientation,
    uiSymbolScale,
    viewportConfig.viewportHeight,
    buttonConfig.height,
    totalSlides,
  ]);

  const paginationConfig = useMemo(
    () => getPaginationConfig(),
    [getPaginationConfig]
  );

  return (
    <>
      {/* Viewport Mask - Widened to allow fade effect */}
      <ViewportMask
        ref={viewportMaskRef}
        width={viewportConfig.maxWidth * 1.5}
        height={viewportConfig.viewportHeight}
      />

      {/* Slides Container - moves horizontally */}
      <Container
        ref={slidesContainerRef}
        name="paytable_slides_container"
        x={0}
        y={0}
      >
        {/* Clone of last slide, regular slides, clone of first slide */}
        {renderSlide(totalSlides - 1, 0, "clone-last")}
        {Array.from({ length: totalSlides }, (_, i) =>
          renderSlide(i, i + 1, i)
        )}
        {renderSlide(0, totalSlides + 1, "clone-first")}
      </Container>

      {/* Drag Area - Invisible overlay ON TOP of slides for swipe/drag detection */}
      <Graphics
        name="paytable_drag_area"
        draw={(g) => {
          g.clear();
          g.beginFill(0x000000, 0.001); // Nearly invisible but interactive
          g.drawRect(-viewportConfig.maxWidth / 2, 0, viewportConfig.maxWidth, viewportConfig.viewportHeight);
          g.endFill();
        }}
        eventMode="static"
        cursor="grab"
        onpointerdown={handlePointerDown}
        onpointermove={handlePointerMove}
        onpointerup={handlePointerUp}
        onpointerupoutside={handlePointerUp}
      />

      {/* Left Arrow - Rendered AFTER drag area so it's on top */}
      <SliderButton
        direction="left"
        viewportConfig={viewportConfig}
        onClick={handlePrevious}
      />

      {/* Right Arrow - Rendered AFTER drag area so it's on top */}
      <SliderButton
        direction="right"
        viewportConfig={viewportConfig}
        onClick={handleNext}
      />

      {/* Pagination Dots */}
      {totalSlides > 1 && (
        <Container
          name="paytable_pagination"
          x={0}
          y={paginationConfig.paginationY}
        >
          {Array.from({ length: totalSlides }).map((_, index) => {
            const dotX = Math.round(
              paginationConfig.paginationStartX +
                index *
                  (paginationConfig.dotSize + paginationConfig.dotSpacing) +
                paginationConfig.dotSize / 2
            );
            // Map clone positions to real slide indices
            const realSlideIndex =
              currentSlideIndex < 0
                ? totalSlides - 1
                : currentSlideIndex >= totalSlides
                ? 0
                : currentSlideIndex;
            const isActive = index === realSlideIndex;

            return (
              <Graphics
                key={index}
                name={`paytable_dot_${index}`}
                draw={(g) => {
                  g.clear();
                  g.beginFill(isActive ? 0xffffff : 0xfeff00, 1);
                  g.drawCircle(0, 0, paginationConfig.dotSize / 2);
                  g.endFill();
                }}
                x={dotX}
                y={0}
                eventMode="static"
                cursor="pointer"
                alpha={1}
                onpointertap={() => handleDotClick(index)}
              />
            );
          })}
        </Container>
      )}
    </>
  );
};

export default PaytableSlider;
